import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import clientRequirementsService from '../../../services/clientRequirementsService';

const CreateRequirementModal = ({
  isOpen = false,
  onClose,
  onRequirementCreated,
  preSelectedClientId = null,
  // Edit mode props
  initialData = null,
  requirementId = null
}) => {
  const isEditMode = !!requirementId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);

  const emptyForm = {
    client_id: preSelectedClientId || '',
    title: '',
    position_title: '',
    department: '',
    job_description: '',
    required_skills: '',
    preferred_skills: '',
    experience_level: '',
    education_requirements: '',
    quantity_needed: 1,
    budget_min: '',
    budget_max: '',
    location: '',
    employment_type: 'full_time',
    start_date: '',
    deadline: '',
    priority: 'medium',
    status: 'open',
    additional_requirements: ''
  };

  const [formData, setFormData] = useState(emptyForm);

  // Load clients when modal opens; populate form in edit mode
  useEffect(() => {
    if (isOpen) {
      loadClients();
      if (isEditMode && initialData) {
        // Convert skills arrays back to comma-separated strings for the text inputs
        const reqSkills = Array.isArray(initialData.required_skills)
          ? initialData.required_skills.join(', ')
          : (initialData.required_skills || '');
        const prefSkills = Array.isArray(initialData.preferred_skills)
          ? initialData.preferred_skills.join(', ')
          : (initialData.preferred_skills || '');

        setFormData({
          client_id: initialData.client_id || preSelectedClientId || '',
          title: initialData.title || '',
          position_title: initialData.position_title || '',
          department: initialData.department || '',
          job_description: initialData.job_description || '',
          required_skills: reqSkills,
          preferred_skills: prefSkills,
          experience_level: initialData.experience_level || '',
          education_requirements: initialData.education_requirements || '',
          quantity_needed: initialData.quantity_needed || 1,
          budget_min: initialData.budget_min || '',
          budget_max: initialData.budget_max || '',
          location: initialData.location || '',
          employment_type: initialData.employment_type || 'full_time',
          start_date: initialData.start_date || '',
          deadline: initialData.deadline || '',
          priority: initialData.priority || 'medium',
          status: initialData.status || 'open',
          additional_requirements: initialData.additional_requirements || ''
        });
      } else if (!isEditMode) {
        setFormData({ ...emptyForm, client_id: preSelectedClientId || '' });
      }
    }
  }, [isOpen, isEditMode, initialData, preSelectedClientId]);

  const loadClients = async () => {
    try {
      const result = await clientRequirementsService.getClients();
      if (result.success) {
        setClients(result.data);
      }
    } catch (err) {
      console.log('Failed to load clients:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Process skills arrays
      const processedData = {
        ...formData,
        required_skills: formData.required_skills
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0),
        preferred_skills: formData.preferred_skills
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0),
        quantity_needed: parseInt(formData.quantity_needed) || 1,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
      };

      let result;
      if (isEditMode) {
        result = await clientRequirementsService.updateRequirement(requirementId, processedData);
      } else {
        result = await clientRequirementsService.createRequirement(processedData);
      }
      
      if (result.success) {
        onRequirementCreated?.();
        if (!isEditMode) resetForm();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(isEditMode ? 'Failed to update requirement' : 'Failed to create requirement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: preSelectedClientId || '',
      title: '',
      position_title: '',
      department: '',
      job_description: '',
      required_skills: '',
      preferred_skills: '',
      experience_level: '',
      education_requirements: '',
      quantity_needed: 1,
      budget_min: '',
      budget_max: '',
      location: '',
      employment_type: 'full_time',
      start_date: '',
      deadline: '',
      priority: 'medium',
      additional_requirements: ''
    });
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  if (!isOpen) return null;

  const clientOptions = [
    { value: '', label: 'Select Client' },
    ...clients.map(client => ({
      value: client.id,
      label: client.company_name
    }))
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const employmentTypeOptions = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'temporary', label: 'Temporary' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block transform overflow-hidden rounded-lg bg-background text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
          {/* Header */}
          <div className="border-b bg-background px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground">
                {isEditMode ? 'Edit Requirement' : 'Create New Client Requirement'}
              </h3>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={handleClose}
              />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-6 max-h-96 overflow-y-auto">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Client *
                    </label>
                    <Select
                      value={formData.client_id}
                      onChange={(value) => handleInputChange('client_id', value)}
                      options={clientOptions}
                      required
                      disabled={!!preSelectedClientId}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Priority *
                    </label>
                    <Select
                      value={formData.priority}
                      onChange={(value) => handleInputChange('priority', value)}
                      options={priorityOptions}
                      required
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Requirement Title *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="e.g., Senior React Developers for Product Team"
                    required
                  />
                </div>
              </div>

              {/* Position Details */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Position Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Position Title *
                    </label>
                    <Input
                      value={formData.position_title}
                      onChange={(e) => handleInputChange('position_title', e.target.value)}
                      placeholder="e.g., Senior Software Engineer"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Department
                    </label>
                    <Input
                      value={formData.department}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      placeholder="e.g., Engineering"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Quantity Needed *
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.quantity_needed}
                      onChange={(e) => handleInputChange('quantity_needed', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Experience Level *
                    </label>
                    <Input
                      value={formData.experience_level}
                      onChange={(e) => handleInputChange('experience_level', e.target.value)}
                      placeholder="e.g., 3-5 years"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Employment Type
                    </label>
                    <Select
                      value={formData.employment_type}
                      onChange={(value) => handleInputChange('employment_type', value)}
                      options={employmentTypeOptions}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Job Description *
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    rows="4"
                    value={formData.job_description}
                    onChange={(e) => handleInputChange('job_description', e.target.value)}
                    placeholder="Describe the role, responsibilities, and expectations..."
                    required
                  />
                </div>
              </div>

              {/* Skills & Requirements */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Skills & Requirements</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Required Skills * <span className="text-sm text-muted-foreground">(comma-separated)</span>
                    </label>
                    <Input
                      value={formData.required_skills}
                      onChange={(e) => handleInputChange('required_skills', e.target.value)}
                      placeholder="React.js, Node.js, JavaScript, MongoDB"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Preferred Skills <span className="text-sm text-muted-foreground">(comma-separated)</span>
                    </label>
                    <Input
                      value={formData.preferred_skills}
                      onChange={(e) => handleInputChange('preferred_skills', e.target.value)}
                      placeholder="TypeScript, GraphQL, AWS, Docker"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Education Requirements
                    </label>
                    <Input
                      value={formData.education_requirements}
                      onChange={(e) => handleInputChange('education_requirements', e.target.value)}
                      placeholder="e.g., BTech/BE in Computer Science or equivalent"
                    />
                  </div>
                </div>
              </div>

              {/* Location & Budget */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Location & Budget</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Location *
                    </label>
                    <Input
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="e.g., Mumbai, Maharashtra"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Budget Min (₹)
                    </label>
                    <Input
                      type="number"
                      value={formData.budget_min}
                      onChange={(e) => handleInputChange('budget_min', e.target.value)}
                      placeholder="80000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Budget Max (₹)
                    </label>
                    <Input
                      type="number"
                      value={formData.budget_max}
                      onChange={(e) => handleInputChange('budget_max', e.target.value)}
                      placeholder="120000"
                    />
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Timeline</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Start Date
                    </label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Deadline *
                    </label>
                    <Input
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => handleInputChange('deadline', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Additional Requirements */}
              <div>
                <h4 className="text-lg font-medium text-foreground mb-4">Additional Requirements</h4>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows="3"
                  value={formData.additional_requirements}
                  onChange={(e) => handleInputChange('additional_requirements', e.target.value)}
                  placeholder="Any additional requirements, preferences, or notes..."
                />
              </div>

              {/* Status (edit mode only) */}
              {isEditMode && (
                <div>
                  <h4 className="text-lg font-medium text-foreground mb-4">Status</h4>
                  <Select
                    value={formData.status}
                    onChange={(value) => handleInputChange('status', value)}
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'partially_filled', label: 'Partially Filled' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'on_hold', label: 'On Hold' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ]}
                  />
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="border-t bg-muted/20 px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                onClick={handleSubmit}
                loading={loading}
                iconName={isEditMode ? 'Save' : 'Plus'}
                iconPosition="left"
              >
                {isEditMode ? 'Save Changes' : 'Create Requirement'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRequirementModal;